import { SUBSCRIPTION_BOOKING_ERROR } from '@hedepy/common/enums/SUBSCRIPTION'
import { type Dayjs } from 'dayjs'
import Markdown from 'markdown-to-jsx'

import Icon from '@/legacy-src/components/icons/Icon'
import WarningBox from '@/legacy-src/components/WarningBox'
import useSubscription from '@/legacy-src/hooks/useSubscription/useSubscription'
import { type ServiceType } from '@/legacy-src/types/GeneralStateType'
import { type TherapistType } from '@/legacy-src/types/TherapistStateType'
import { dateToMonthYear } from '@/legacy-src/utilities/date'
import { getTranslate } from '@/legacy-src/utilities/i18n'

import { useCartResolve } from '@/src/domains/cart/hooks/useCartResolve'

import { isResolvedOrdinaryEvent } from './utils'

type SubscriptionDiffersInPriceProps = {
  month: Dayjs
  therapist: TherapistType
  service: ServiceType
}

export const SubscriptionDiffersInPrice = ({ month, therapist, service }: SubscriptionDiffersInPriceProps) => {
  const { t } = getTranslate()
  const { getSubscription } = useSubscription()
  const monthYear = dateToMonthYear(month)
  const subscription = getSubscription(monthYear)
  const price = therapist.prices?.find((price) => price.serviceId === service.id)?.price

  if (!subscription || price === subscription.initPrice) return null

  return (
    <WarningBox
      text={t('component.subscription.cart.notSamePrice', {
        price: t('general.currency', { currency: subscription.initPrice }),
      })}
    />
  )
}

export const CoveredWithSubscription = () => {
  const { t } = getTranslate()
  return (
    <div className="flex items-center text-xs">
      <Icon icon="check-small" size="xs" className="mr-1 rounded-full bg-blue-500" />
      <Markdown>{t('component.subscription.cart.covered')}</Markdown>
    </div>
  )
}

export const NotCoveredWithSubscription = () => {
  const { t } = getTranslate()

  return (
    <div className="flex items-center text-xs">
      <Icon icon="info-small" size="xs" className="mr-1 rounded-full bg-yellow-500" />
      <Markdown>{t('component.subscription.cart.notCovered')}</Markdown>
    </div>
  )
}

export const NotAllCoveredWithSubscription = () => {
  const { t } = getTranslate()
  const { data: resolvedCart } = useCartResolve()

  const reason = resolvedCart?.events
    ?.filter(isResolvedOrdinaryEvent)
    .map((event) => event.notDiscountedReasons)
    .flat()
    .find((reason) => reason?.type === 'subscription')

  if (reason?.error === SUBSCRIPTION_BOOKING_ERROR.EXCEEDED_EVENT_BOOKED) {
    return <WarningBox text={t('component.subscription.cart.notEnough')} />
  }

  if (reason?.error === SUBSCRIPTION_BOOKING_ERROR.USER_SUBSCRIPTION_NOT_VALID) {
    return <WarningBox text={t('component.subscription.cart.notValid')} />
  }

  if (reason?.error === SUBSCRIPTION_BOOKING_ERROR.EVENT_PRICE_DIFFERENT_THAN_IN_SUBSCRIPTION) {
    const initPrice = resolvedCart?.subscriptions?.[0]?.initPrice

    return (
      <WarningBox
        text={t('component.subscription.cart.notSamePrice', { price: t('general.currency', { currency: initPrice }) })}
      />
    )
  }

  return null
}

export const SavedWithSubscription = () => {
  const { t } = getTranslate()
  const { data: resolvedCart } = useCartResolve()

  const savedAmount = resolvedCart?.subscriptions?.reduce((total, subscription) => {
    return total + parseFloat(subscription.savedWithSubscriptionByThisBooking)
  }, 0)

  if (!savedAmount) return null

  return (
    <div className="flex w-full items-center gap-4 rounded-xl bg-blue-500 p-5">
      <div className="p-1.5">
        <Icon icon="hedepy-plus" size="md" className="text-white" />
      </div>
      <div className="text-sm font-bold">
        {t('component.subscription.cart.saved', {
          amount: t('general.currency', { currency: savedAmount }),
        })}
      </div>
    </div>
  )
}
